import React, { ReactNode } from 'react';
import { useAppSelector } from 'hooks';
import classNames from 'classnames';

import { TooltipPortal } from '~components';
import Icon, { IconProp } from '~components/icon';

import { selectUserTags } from '~main/selectors/login';

export interface ClickableIconProps {
  id: string | number;
  icon: IconProp;
  shape?: 'square' | 'round';
  size?: 'x-small' | 'small' | 'medium' | 'large';
  toolTip?: ReactNode;
  onClick?: (val?: any) => void;
  color?: string | null;
  className?: string;
  iconClassname?: string;
  disabled?: boolean;
  showTooltipWhenDisabled?: boolean;
  dataCy?: string;
  authTag?: string;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  href?: string;
}
/**
 * A clickable icon component with optional tooltip and customizable size/shape.
 * @param {string | number} props.id - Unique identifier for the icon.
 * @param {IconProp} props.icon - The icon to display (passed to the `Icon` component).
 * @param {'square' | 'round'} [props.shape='round'] - Defines the icon's shape. Default is `'round'`.
 * @param {'small' | 'medium' | 'large'} [props.size='small'] - Controls the icon size. Default is `'small'`.
 * @param {ReactNode} [props.toolTip] - Tooltip text or component to display on hover.
 * @param {() => void} [props.onClick] - Click handler for the icon.
 * @param {boolean} [props.disabled]- Disabled the icon
 * @param {string | null} [props.color='black'] - Color of the icon, default is `'black'`, can be null for css styling.
 * @param {string | null} [props.authTag] Authorization tag that restricts button visibility to users with this tag
 * @param {string} [props.href] - When set (and not `disabled`), renders a real `<a href>` instead of a
 *   `<div role="button">` so the browser's native "open in new tab/window" gestures work on right-click,
 *   middle-click, and Ctrl/Cmd/Shift+click. A plain, unmodified, primary-button left-click is still
 *   intercepted (`preventDefault` + `onClick`) for in-app SPA navigation — mirrors the `linkCellRender`
 *   click-guard pattern (TAV-262). Omitting `href`, or setting `disabled`, keeps the existing
 *   `<div role="button">` markup unchanged.
 * @returns {JSX.Element} The clickable icon component.
 */

export const ClickableIcon: React.FC<ClickableIconProps> = ({
  id,
  icon,
  className: wrapperClassName = '',
  iconClassname = '',
  shape = 'round',
  size = 'small',
  toolTip,
  onClick,
  color = 'black',
  disabled = false,
  showTooltipWhenDisabled = false,
  dataCy,
  authTag,
  ariaLabel,
  ariaExpanded,
  href,
}) => {
  const tags = useAppSelector(selectUserTags);
  const visible = !authTag || (authTag && tags.includes(authTag));

  const getSizeClasses = (size: 'x-small' | 'small' | 'medium' | 'large') => ({
    sizeClass: `icon-${size}`,
    clickableSizeClass: `clickable-${size}`,
  });

  const { sizeClass, clickableSizeClass } = getSizeClasses(size);

  const shapeClass = shape === 'round' ? 'icon-round' : 'icon-square';

  const target = `clickable-icon-${id}`;
  const className = classNames(
    [sizeClass, iconClassname],
    { [`text-${color}`]: color !== null },
    { 'icon-disabled': disabled },
    { disabled },
  );
  const wrapperClassNames = classNames(['clickable-icon', clickableSizeClass, shapeClass, wrapperClassName], {
    disabled: disabled && !showTooltipWhenDisabled,
    'disabled-no-hover': disabled && showTooltipWhenDisabled,
  });

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) {
      event?.preventDefault?.();
      return;
    }
    onClick?.();
  };

  if (!visible) return null;

  const tooltipPortal = toolTip && (!disabled || showTooltipWhenDisabled) && (
    <TooltipPortal targetId={target} content={toolTip} />
  );
  const iconNode = <Icon icon={icon} id={`icon-${size}`} className={className} />;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const { key } = event;
    if (!disabled && (key === 'Enter' || key === ' ')) {
      // preventDefault stops the browser's native Enter-key click synthesis on the anchor
      // variant's <a href> (which would otherwise fire the anchor's onClick handler a
      // second time on top of the explicit onClick() call below).
      event.preventDefault();
      onClick?.();
    }
  };

  // href is only honoured when the icon isn't disabled — a disabled icon must never
  // advertise a working right-click "open in new tab" link (matches current disabled semantics).
  if (href && !disabled) {
    return (
      <a
        href={href}
        data-cy={dataCy}
        className={wrapperClassNames}
        id={target}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        onClick={(event) => {
          // Let the browser handle modifier / non-primary-button clicks so native
          // new-tab / new-window gestures keep working (mirrors linkCellRender, TAV-262).
          if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;
          event.preventDefault();
          onClick?.();
        }}
        onKeyDown={handleKeyDown}
        style={{ userSelect: 'none' }}
      >
        {tooltipPortal}
        {iconNode}
      </a>
    );
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-cy={dataCy}
      className={wrapperClassNames}
      id={target}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{ userSelect: 'none' }}
    >
      {tooltipPortal}
      {iconNode}
    </div>
  );
};

export default ClickableIcon;
